import { FC, Fragment, MouseEventHandler, useReducer, useState } from 'react';

import { BadgePercent, ChevronDown, CirclePercent, Plus, SquarePercent } from 'lucide-react';

import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import { AddPromoCodesGroupDialog } from './addPromoCodesGroupDialog/AddPromoCodesGroupDialog';
import { AddSinglePromoCodeDialog } from './addSinglePromoCodeDialog/AddSinglePromoCodeDialog';
import { AddUniquePromoCodesDialog } from './addUniquePromoCodesDialog/AddUniquePromoCodesDialog';

import css from './AddPromoCodeMenu.module.css';

import { Nullable } from 'v2/types';

interface AddPromoCodeMenuProps {
  onRevalidate: VoidFunction;
}

export const AddPromoCodeMenu: FC<AddPromoCodeMenuProps> = ({ onRevalidate }) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const [isOpenSinglePromoCodeDialog, setOpenSinglePromoCodeDialog] = useReducer((state) => !state, false);
  const [isOpenUniquePromoCodesDialog, setOpenUniquePromoCodesDialog] = useReducer((state) => !state, false);
  const [isOpenGroupPromoCodesDialog, setOpenGroupPromoCodesDialog] = useReducer((state) => !state, false);

  const handleClick: MouseEventHandler<HTMLElement> = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (cb: VoidFunction) => () => {
    handleClose();
    cb();
  };

  return (
    <Fragment>
      <Button
        onClick={handleClick}
        startIcon={<Plus />}
        endIcon={<ChevronDown width={20} height={20} />}
        variant="contained"
        size="large"
      >
        Add
      </Button>

      <Menu
        slotProps={{ paper: { className: css.addPromoCodeMenu } }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
      >
        <MenuItem onClick={handleMenuItemClick(setOpenSinglePromoCodeDialog)}>
          <CirclePercent className={css.addPromoCodeMenuItemIcon} width={20} />
          Single Promo Code
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleMenuItemClick(setOpenUniquePromoCodesDialog)}>
          <BadgePercent className={css.addPromoCodeMenuItemIcon} width={20} />
          Unique Promo Codes Group
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleMenuItemClick(setOpenGroupPromoCodesDialog)}>
          <SquarePercent className={css.addPromoCodeMenuItemIcon} width={18} />
          Promo Codes Group
        </MenuItem>
      </Menu>

      <AddSinglePromoCodeDialog
        isOpen={isOpenSinglePromoCodeDialog}
        onClose={setOpenSinglePromoCodeDialog}
        onRevalidate={onRevalidate}
      />

      <AddUniquePromoCodesDialog
        isOpen={isOpenUniquePromoCodesDialog}
        onClose={setOpenUniquePromoCodesDialog}
        onRevalidate={onRevalidate}
      />

      <AddPromoCodesGroupDialog
        isOpen={isOpenGroupPromoCodesDialog}
        onClose={setOpenGroupPromoCodesDialog}
        onRevalidate={onRevalidate}
      />
    </Fragment>
  );
};
